import { CSSProperties, useMemo } from "react";

/**
 * Confetti cluster in the Eid Al Etihad style (p33): thin straight and curved strips
 * in flag colors. Place it ONLY on a container's corner or step (the guideline anchors
 * confetti to frames). Same seed = same pattern on every render (safe for react-snap).
 */

const COLORS = [
  "#B8293A",
  "#36844A",
  "#151A20",
  "#FFFFFF",
  "#36844A",
  "#B8293A",
  "#FFFFFF",
];

interface Props {
  seed?: number;
  count?: number;
  /** share of curved strips, 0–1 */
  curved?: number;
  className?: string;
  style?: CSSProperties;
}

export default function EidConfetti({
  seed = 7,
  count = 30,
  curved: curvedShare = 0,
  className,
  style,
}: Props) {
  const pieces = useMemo(() => {
    let s = seed;
    const r = () => ((s = (s * 9301 + 49297) % 233280), s / 233280);
    // soft spread: a little denser at the anchor
    const g = () => (r() + r()) / 2;
    return Array.from({ length: count }, (_, i) => {
      const x = 4 + g() * 92;
      const y = 4 + g() * 92;
      const w = 2.4 + r() * 2.6;
      const h = 0.9 + r() * 0.5;
      const a = Math.round(r() * 180);
      const curved = r() < curvedShare;
      return { x, y, w, h, a, curved, c: COLORS[i % COLORS.length] };
    });
  }, [seed, count, curvedShare]);

  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 100 100"
      aria-hidden="true"
      focusable="false"
    >
      {pieces.map((p, i) =>
        p.curved ? (
          <path
            key={i}
            d={`M ${p.x} ${p.y} q ${p.w / 2} ${-p.w / 2.2} ${p.w} 0`}
            fill="none"
            stroke={p.c}
            strokeWidth={p.h}
            strokeLinecap="butt"
            transform={`rotate(${p.a} ${p.x} ${p.y})`}
          />
        ) : (
          <rect
            key={i}
            x={p.x}
            y={p.y}
            width={p.w}
            height={p.h}
            fill={p.c}
            transform={`rotate(${p.a} ${p.x} ${p.y})`}
          />
        ),
      )}
    </svg>
  );
}
